import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import api from '../services/api';
import { AuthContext } from './AuthContext';

const SessionContext = createContext();

export const SessionProvider = ({ children }) => {
  const { token, role, logIn, logOut } = useContext(AuthContext);
  const [showInactivityModal, setShowInactivityModal] = useState(false);
  const [showLogoutNotification, setShowLogoutNotification] = useState(false);
  const [countdownTime, setCountdownTime] = useState(0);

  const inactivityTimeout = useRef(null);
  const countdownInterval = useRef(null);
  const activityDebounce = useRef(null);
  const logoutNotificationTimeout = useRef(null);
  const storagePreference = useRef('session');
  const lastRefreshAt = useRef(0);

  // Never refresh more often than this, no matter how much the user clicks.
  const MIN_REFRESH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
  // How long after the last activity before we try a silent refresh.
  const ACTIVITY_REFRESH_DEBOUNCE_MS = 60 * 1000; // 1 minute

  const isLoggedIn = Boolean(token);
  const warningDurationMs = useMemo(() => {
    if (!isLoggedIn) return 0;
    return (role === 'admin' ? 10 : 5) * 60 * 1000;
  }, [isLoggedIn, role]);

  const sessionLifetimeMs = 30 * 60 * 1000;

  useEffect(() => {
    if (localStorage.getItem('accessToken')) {
      storagePreference.current = 'local';
      return;
    }

    if (sessionStorage.getItem('accessToken')) {
      storagePreference.current = 'session';
    }
  }, [token]);

  const clearTimers = useCallback(() => {
    if (inactivityTimeout.current) {
      clearTimeout(inactivityTimeout.current);
      inactivityTimeout.current = null;
    }

    if (countdownInterval.current) {
      clearInterval(countdownInterval.current);
      countdownInterval.current = null;
    }

    if (activityDebounce.current) {
      clearTimeout(activityDebounce.current);
      activityDebounce.current = null;
    }
  }, []);

  const storeAccessToken = useCallback(
    (accessToken) => {
      const userId = localStorage.getItem('userId') || sessionStorage.getItem('userId');
      const rememberMe = storagePreference.current === 'local';
      logIn(accessToken, userId, rememberMe);
    },
    [logIn]
  );

  const handleInactivityLogout = useCallback(async () => {
    clearTimers();
    setShowInactivityModal(false);
    setCountdownTime(0);
    setShowLogoutNotification(true);

    if (logoutNotificationTimeout.current) {
      clearTimeout(logoutNotificationTimeout.current);
    }

    logoutNotificationTimeout.current = setTimeout(() => {
      setShowLogoutNotification(false);
    }, 5000);

    await logOut();
  }, [clearTimers, logOut]);

  const startCountdown = useCallback(() => {
    if (!warningDurationMs) return;

    setCountdownTime(Math.ceil(warningDurationMs / 1000));

    countdownInterval.current = setInterval(() => {
      setCountdownTime((prev) => {
        if (prev <= 1) {
          if (countdownInterval.current) {
            clearInterval(countdownInterval.current);
            countdownInterval.current = null;
          }
          void handleInactivityLogout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [handleInactivityLogout, warningDurationMs]);

  const resetInactivityTimer = useCallback(() => {
    clearTimers();
    setShowInactivityModal(false);
    setCountdownTime(0);

    // "Remember me" sessions stay signed in until the 30-day refresh cookie ends.
    if (!isLoggedIn || !warningDurationMs || storagePreference.current === 'local') return;

    const activeWindowMs = Math.max(sessionLifetimeMs - warningDurationMs, 0);

    inactivityTimeout.current = setTimeout(() => {
      setShowInactivityModal(true);
      startCountdown();
    }, activeWindowMs);
  }, [clearTimers, isLoggedIn, sessionLifetimeMs, startCountdown, warningDurationMs]);

  const refreshToken = useCallback(
    async ({ force = false } = {}) => {
      // Throttle: skip silent refreshes if we just refreshed recently.
      const now = Date.now();
      if (!force && now - lastRefreshAt.current < MIN_REFRESH_INTERVAL_MS) {
        resetInactivityTimer();
        return true;
      }

      try {
        const { data } = await api.post('/user/refresh', {}, { withCredentials: true });
        lastRefreshAt.current = Date.now();
        storeAccessToken(data.accessToken);
        resetInactivityTimer();
        return true;
      } catch (error) {
        // Do NOT force-logout on a transient refresh failure. The current
        // access token may still be valid, and the global api interceptor
        // will handle real 401/403 responses when they actually happen.
        console.warn('Silent token refresh failed; keeping current session.', error);
        return false;
      }
    },
    [MIN_REFRESH_INTERVAL_MS, resetInactivityTimer, storeAccessToken]
  );

  const handleStayLoggedIn = useCallback(async () => {
    const ok = await refreshToken({ force: true });
    if (!ok) {
      // Only force-logout from the explicit modal path if refresh truly
      // failed — at that point the user has been idle for a while.
      await handleInactivityLogout();
    }
  }, [handleInactivityLogout, refreshToken]);

  const handleUserActivity = useCallback(() => {
    if (showInactivityModal) return;

    if (activityDebounce.current) {
      clearTimeout(activityDebounce.current);
    }

    activityDebounce.current = setTimeout(() => {
      void refreshToken();
    }, ACTIVITY_REFRESH_DEBOUNCE_MS);
  }, [ACTIVITY_REFRESH_DEBOUNCE_MS, refreshToken, showInactivityModal]);

  useEffect(() => {
    if (!isLoggedIn) {
      clearTimers();
      setShowInactivityModal(false);
      setCountdownTime(0);
      return;
    }

    // Seed the throttle clock so we don't immediately refresh right after
    // login — the access token we just got is good for 30 minutes.
    lastRefreshAt.current = Date.now();

    // Note: `mousemove` is intentionally excluded — it fires far too often
    // and was causing the session layer to hammer /user/refresh.
    const events = ['click', 'keydown', 'scroll', 'touchstart'];
    events.forEach((event) => {
      document.addEventListener(event, handleUserActivity);
    });

    resetInactivityTimer();

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleUserActivity);
      });

      clearTimers();
    };
  }, [clearTimers, handleUserActivity, isLoggedIn, resetInactivityTimer]);

  useEffect(() => () => {
    if (logoutNotificationTimeout.current) {
      clearTimeout(logoutNotificationTimeout.current);
    }
  }, []);

  return (
    <SessionContext.Provider
      value={{
        showInactivityModal,
        showLogoutNotification,
        countdownTime,
        handleStayLoggedIn,
        handleInactivityLogout,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
};

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within SessionProvider');
  }
  return context;
};
